import { BoxProps, Flex, HStack, Icon, Square, useDisclosure } from '@televet/kibble-ui';
import React, { useCallback, useRef } from 'react';
import { ChromePicker } from 'react-color';
import { useOutsideClick } from 'shared/hooks/useOutsideClick';
import { colorContrast } from 'shared/utils';

interface Color {
  name: string;
  hex: string;
}

export const defaultColors: Color[] = [
  { name: 'Red', hex: '#E01F1F' },
  { name: 'Pink', hex: '#e47792' },
  { name: 'Orange', hex: '#f08f02' },
  { name: 'Yellow', hex: '#edc308' },
  { name: 'Green', hex: '#5cc098' },
  { name: 'Cyan', hex: '#39adc3' },
  { name: 'Blue', hex: '#72abff' },
  { name: 'Purple', hex: '#95a2f9' },
  { name: 'Gray', hex: '#878890' },
  { name: 'Black', hex: '#484848' },
];

interface ColorPickerProps {
  selectedColor: string;
  onSelectColor: (color: string) => void;
  closeColorPicker?: (color: string) => void;
  colorList?: Color[];
  containerStyles?: BoxProps;
}

export const ColorPicker = ({
  selectedColor,
  onSelectColor,
  closeColorPicker,
  colorList = defaultColors,
  containerStyles,
}: ColorPickerProps): JSX.Element => {
  const wrapperRef = useRef(null);
  const {
    isOpen: isCustomColorPickerOpen,
    onClose: onCloseCustomColorPicker,
    onToggle: onToggleCustomColorPicker,
  } = useDisclosure();

  useOutsideClick(wrapperRef, () => {
    if (closeColorPicker) closeColorPicker(selectedColor);
    onCloseCustomColorPicker();
  });

  const handleToggleCustomColorPicker = useCallback(() => {
    onToggleCustomColorPicker();
  }, [onToggleCustomColorPicker]);

  const handleDefaultColorClick = useCallback(
    (color: string) => {
      onSelectColor(color);
      if (closeColorPicker) closeColorPicker(color);
    },
    [closeColorPicker, onSelectColor],
  );

  const handleCustomColorClick = useCallback((color: string) => onSelectColor(color), [onSelectColor]);

  return (
    <HStack
      className="ColorPicker__Selector"
      justify="space-evenly"
      align="center"
      spacing={2}
      p={1}
      ref={wrapperRef}
      {...containerStyles}
    >
      {colorList.map((color, index) => {
        return (
          <Square
            data-testid={`color-picker-color-option-${index}`}
            className="ColorPicker__ColorOption"
            key={`default-color-${index}-${color}`}
            size={6}
            borderRadius={2}
            bg={color.hex}
            _hover={{ boxShadow: '0 0 0 2px var(--chakra-colors-border-default)' }}
            onClick={(): void => handleDefaultColorClick(color.hex)}
          />
        );
      })}
      <Square
        data-testid="color-picker-color-custom-option"
        className="ColorPicker__CustomColor"
        size={6}
        borderRadius={2}
        bg={selectedColor}
        _hover={{ boxShadow: '0 0 0 2px var(--chakra-colors-border-default)' }}
        onClick={handleToggleCustomColorPicker}
      >
        <Icon size="xs" name="eyedropper" color={colorContrast(selectedColor) || 'text.default'} />
      </Square>
      {isCustomColorPickerOpen && (
        <Flex className="ColorPicker__ChromePickerContainer" pos="absolute" top={9}>
          <ChromePicker color={selectedColor} onChange={(color): void => handleCustomColorClick(color.hex)} />
        </Flex>
      )}
    </HStack>
  );
};
